import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { ShieldBan, Plus, History, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useApi, useAuth } from '@/contexts/AuthContext';

const C = { navy: '#0D1B2A', slate: '#5C6B7A', green: '#1D9E75', amber: '#E8A020', red: '#E24B4A', blue: '#1A4C7C', teal: '#00B4D8' };
const fmt = iso => (iso ? new Date(iso).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

function AddForm({ api, onAdded }) {
  const [term, setTerm] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const add = async () => {
    setBusy(true);
    try {
      const r = await api.post('/embargo/extensions', { term, reason });
      toast.success(`'${r.data.term_norm}' added — marine declarations touching it now hard-decline`);
      setTerm(''); setReason(''); onAdded();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to add'); }
    setBusy(false);
  };
  return (
    <div className="p-3 rounded-sm space-y-2" style={{ background: 'rgba(226,75,74,0.04)', border: '1px solid rgba(226,75,74,0.15)' }} data-testid="embargo-add-form">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <input value={term} onChange={e => setTerm(e.target.value)} placeholder="Jurisdiction / port / city (e.g. Port Sudan)"
          data-testid="embargo-term-input" className="px-2 py-1.5 border border-input rounded-sm text-xs bg-white" />
        <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason for the audit trail (min 10 chars)"
          data-testid="embargo-reason-input" className="px-2 py-1.5 border border-input rounded-sm text-xs bg-white md:col-span-2" />
      </div>
      <div className="flex items-center gap-3">
        <button onClick={add} disabled={busy || term.trim().length < 3 || reason.trim().length < 10}
          data-testid="embargo-add-submit-btn"
          className="px-4 py-1.5 rounded-sm text-xs font-medium text-white disabled:opacity-40" style={{ background: C.red }}>
          {busy ? 'Adding…' : 'Add to embargo list'}
        </button>
        <span className="text-[11px]" style={{ color: C.slate }}>{reason.trim().length < 10 ? `${10 - reason.trim().length} more characters of reason needed` : 'Takes effect on the next declaration'}</span>
      </div>
    </div>
  );
}

function RemoveButton({ api, ext, onRemoved }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const remove = async () => {
    try {
      await api.post(`/embargo/extensions/${ext.extension_id}/remove`, { reason });
      toast.success(`'${ext.term_norm}' removed — retained in the audit trail`);
      onRemoved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to remove'); }
  };
  if (!open) {
    return <button onClick={() => setOpen(true)} data-testid={`embargo-remove-btn-${ext.extension_id}`}
      className="px-2 py-0.5 rounded text-[10px] font-medium" style={{ background: 'rgba(226,75,74,0.1)', color: C.red }}>Remove</button>;
  }
  return (
    <span className="flex items-center gap-1.5">
      <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Removal reason (min 10)"
        data-testid={`embargo-remove-reason-${ext.extension_id}`}
        className="px-2 py-1 border border-input rounded-sm text-[10px] bg-white w-44" />
      <button onClick={remove} disabled={reason.trim().length < 10} data-testid={`embargo-remove-confirm-${ext.extension_id}`}
        className="px-2 py-0.5 rounded text-[10px] font-medium text-white disabled:opacity-40" style={{ background: C.red }}>Confirm</button>
      <button onClick={() => setOpen(false)} className="text-[10px] underline" style={{ color: C.slate }}>cancel</button>
    </span>
  );
}

export default function EmbargoListPage() {
  const api = useApi();
  const { user } = useAuth();
  const canManage = ['platform_owner', 'mga_admin'].includes(user?.role);
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState([]);

  const load = useCallback(() => {
    api.get('/embargo/list').then(r => setData(r.data)).catch(() => {});
    api.get('/embargo/audit').then(r => setAudit(r.data.audit)).catch(() => {});
  }, [api]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="text-xs py-6 text-center" style={{ color: C.slate }}>Loading embargo register…</p>;

  return (
    <div className="space-y-4" data-testid="embargo-list-page">
      <Card className="p-4 rounded-md space-y-3" style={{ border: '1px solid rgba(226,75,74,0.2)' }}>
        <div className="flex items-start gap-2">
          <ShieldBan className="w-4 h-4 mt-0.5" style={{ color: C.red }} />
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Sanctioned Jurisdictions — Marine Embargo Screen</h3>
            <p className="text-[11px]" style={{ color: C.slate }}>
              Every marine declaration screens origin and destination against this register. A hit is a hard decline — not releasable.
              The base list is immutable; your tenant may only extend it.
            </p>
          </div>
        </div>

        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider mb-1.5 flex items-center gap-1" style={{ color: C.slate }}>
            <Lock className="w-3 h-3" /> Base list — {data.base_source}
          </p>
          <div className="flex flex-wrap gap-1" data-testid="embargo-base-list">
            {data.base.map(t => (
              <span key={t} className="px-1.5 py-0.5 rounded text-[10px] font-medium"
                style={{ background: 'rgba(13,27,42,0.06)', color: C.navy }}>{t}</span>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: C.slate }}>
            Tenant extensions ({data.extensions.length} active)
          </p>
          {canManage && <AddForm api={api} onAdded={load} />}
          {data.extensions.length === 0 ? (
            <p className="text-xs py-1" style={{ color: C.slate }} data-testid="embargo-extensions-empty">No tenant extensions — the base list applies as-is.</p>
          ) : (
            <table className="w-full text-xs" data-testid="embargo-extensions-table">
              <thead><tr className="text-left" style={{ color: C.slate }}>
                <th className="py-1.5 pr-2 font-medium">Jurisdiction</th><th className="py-1.5 pr-2 font-medium">Reason</th>
                <th className="py-1.5 pr-2 font-medium">Added by</th><th className="py-1.5 pr-2 font-medium">Added</th>
                {canManage && <th className="py-1.5 font-medium">Action</th>}
              </tr></thead>
              <tbody>
                {data.extensions.map(e => (
                  <tr key={e.extension_id} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.1)' }} data-testid={`embargo-ext-row-${e.term_norm}`}>
                    <td className="py-1.5 pr-2 font-semibold" style={{ color: C.red }}>{e.term_norm}</td>
                    <td className="py-1.5 pr-2" style={{ color: C.navy }}>{e.reason}</td>
                    <td className="py-1.5 pr-2" style={{ color: C.slate }}>{e.added_by} ({e.added_by_role})</td>
                    <td className="py-1.5 pr-2" style={{ color: C.slate }}>{fmt(e.added_at)}</td>
                    {canManage && <td className="py-1.5"><RemoveButton api={api} ext={e} onRemoved={load} /></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      <Card className="p-4 rounded-md space-y-2" style={{ border: '1px solid rgba(136,150,168,0.15)' }}>
        <div className="flex items-center gap-2">
          <History className="w-4 h-4" style={{ color: C.blue }} />
          <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Audit Trail</h3>
          <span className="text-[10px]" style={{ color: C.slate }}>{audit.length} entries — removals are retained, never deleted</span>
        </div>
        {audit.length === 0 ? (
          <p className="text-xs py-1" style={{ color: C.slate }} data-testid="embargo-audit-empty">No embargo list changes recorded yet.</p>
        ) : (
          <table className="w-full text-xs" data-testid="embargo-audit-table">
            <thead><tr className="text-left" style={{ color: C.slate }}>
              <th className="py-1.5 pr-2 font-medium">When</th><th className="py-1.5 pr-2 font-medium">Action</th>
              <th className="py-1.5 pr-2 font-medium">Jurisdiction</th><th className="py-1.5 pr-2 font-medium">Actor</th>
              <th className="py-1.5 font-medium">Reason</th>
            </tr></thead>
            <tbody>
              {audit.map(a => (
                <tr key={a.audit_id} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.1)' }} data-testid={`embargo-audit-row-${a.audit_id}`}>
                  <td className="py-1.5 pr-2" style={{ color: C.slate }}>{fmt(a.at)}</td>
                  <td className="py-1.5 pr-2">
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase"
                      style={{ background: a.action === 'added' ? 'rgba(226,75,74,0.1)' : 'rgba(232,160,32,0.12)', color: a.action === 'added' ? C.red : '#A8720E' }}>
                      {a.action}
                    </span>
                  </td>
                  <td className="py-1.5 pr-2 font-semibold" style={{ color: C.navy }}>{a.term}</td>
                  <td className="py-1.5 pr-2" style={{ color: C.slate }}>{a.actor} ({a.actor_role})</td>
                  <td className="py-1.5" style={{ color: C.navy }}>{a.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
